import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { WHATSAPP_LINK } from "../lib/api";
import CarCard from "../components/CarCard";
import { ArrowRight, ShieldCheck, BadgeCheck, Sparkles, Handshake, ChevronRight } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1503376780353-7e6692767b70?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBjYXIlMjBzbGVlayUyMG1vZGVybnxlbnwwfHx8fDE3Nzk0NTUzNTB8MA&ixlib=rb-4.1.0&q=85";

const INTERIOR_IMG =
  "https://images.unsplash.com/photo-1599912027667-755b68b4dd3b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBjYXIlMjBpbnRlcmlvcnxlbnwwfHx8fDE3Nzk0NTUzNTB8MA&ixlib=rb-4.1.0&q=85";

const BRANDS = [
  "Porsche", "Mercedes-Benz", "BMW", "Audi", "Range Rover",
  "Lamborghini", "Bentley", "Jaguar", "Maserati", "Aston Martin", "Ferrari",
];

const VALUE_PROPS = [
  { icon: ShieldCheck, title: "200-Point Inspection", text: "Every car undergoes a forensic mechanical and cosmetic audit before listing." },
  { icon: BadgeCheck, title: "Verified Provenance", text: "RC, insurance and service history validated with documented chain of ownership." },
  { icon: Sparkles, title: "Concierge Delivery", text: "Pan-India doorstep delivery with white-glove handover and orientation." },
  { icon: Handshake, title: "Buy-Back Assurance", text: "Transparent valuations and a guaranteed buy-back option for select listings." },
];

const PROCESS = [
  { step: "01", title: "Discover", text: "Browse hand-picked listings or tell us what you're looking for." },
  { step: "02", title: "Inspect", text: "Schedule a private viewing or request a full digital inspection report." },
  { step: "03", title: "Finance", text: "Tie-ups with leading luxury finance partners and exchange offers." },
  { step: "04", title: "Drive Away", text: "Paperwork handled end-to-end. Doorstep delivery across India." },
];

const TESTIMONIALS = [
  { name: "Rohan M.", car: "Porsche 911 Carrera S", text: "Royale Yaan made buying a pre-owned 911 feel like a brand-new showroom experience. Truly exceptional." },
  { name: "Aditi K.", car: "Range Rover Autobiography", text: "The provenance check and concierge delivery were flawless. I'd never go anywhere else." },
  { name: "Vikram S.", car: "Mercedes S-Class", text: "Sold my S-Class within five days at a fair, transparent price. Painless from start to finish." },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/cars", { params: { featured: true } })
      .then((res) => setFeatured(res.data.slice(0, 6)))
      .catch(() => setFeatured([]));
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative min-h-[88vh] flex items-end overflow-hidden bg-black">
        <img
          src={HERO_IMG}
          alt="Luxury sedan in motion"
          className="absolute inset-0 w-full h-full object-cover opacity-80"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
        <div className="relative z-10 mx-auto max-w-[1400px] w-full px-6 md:px-12 lg:px-16 pb-20 md:pb-28">
          <p className="overline text-white/80 mb-6 ry-fade-up">Curated Luxury · Pre-Owned</p>
          <h1 className="font-display text-white text-5xl sm:text-6xl md:text-7xl lg:text-[88px] font-light leading-[0.95] tracking-tighter max-w-5xl ry-fade-up" style={{animationDelay:'120ms'}}>
            The next chapter of <em className="not-italic gold-accent text-[#C8B68F]">your dream</em> automobile.
          </h1>
          <p className="text-white/80 text-base md:text-lg max-w-2xl mt-8 ry-fade-up" style={{animationDelay:'240ms'}}>
            India&apos;s most discerning marketplace for hand-picked pre-owned luxury automobiles &mdash;
            inspected, authenticated and ready to be re-discovered.
          </p>
          <div className="flex flex-wrap gap-3 mt-10 ry-fade-up" style={{animationDelay:'360ms'}}>
            <Link to="/inventory" data-testid="hero-browse-cta" className="inline-flex items-center gap-2 px-7 py-4 text-sm tracking-wide font-medium rounded-sm bg-white text-black hover:bg-gray-100 transition-colors">
              Browse Inventory <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
            <Link to="/sell" data-testid="hero-sell-cta" className="inline-flex items-center gap-2 px-7 py-4 text-sm tracking-wide font-medium rounded-sm border border-white/40 text-white hover:bg-white/10 transition-colors">
              Sell Your Car
            </Link>
          </div>
        </div>
      </section>

      {/* Brand strip */}
      <section className="border-b border-gray-100 overflow-hidden bg-white">
        <div className="py-10 flex whitespace-nowrap ry-marquee">
          {[...BRANDS, ...BRANDS].map((b, i) => (
            <span key={i} className="mx-12 font-display text-2xl md:text-3xl tracking-tight text-gray-300 hover:text-black transition-colors">
              {b}
            </span>
          ))}
        </div>
      </section>

      {/* Featured inventory */}
      <section className="py-24 md:py-32 bg-[#FCFCFC]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <p className="overline mb-4">The Collection</p>
              <h2 className="font-display text-4xl md:text-5xl tracking-tighter font-light max-w-2xl">
                Featured automobiles, ready to be driven.
              </h2>
            </div>
            <Link to="/inventory" data-testid="featured-view-all" className="inline-flex items-center gap-2 text-sm tracking-wide hover:gap-3 transition-all">
              View full inventory <ArrowRight size={15} strokeWidth={1.5} />
            </Link>
          </div>

          {featured.length === 0 ? (
            <div className="py-20 text-center text-gray-500" data-testid="featured-empty">No featured cars yet.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featured.map((c, i) => <CarCard key={c.id} car={c} idx={i} />)}
            </div>
          )}
        </div>
      </section>

      {/* Why Us */}
      <section className="py-24 md:py-32 bg-white border-y border-gray-100">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-4">
              <p className="overline mb-4">Why Royale Yaan</p>
              <h2 className="font-display text-4xl md:text-5xl tracking-tighter font-light">
                The luxury of trust, in every detail.
              </h2>
            </div>
            <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-12">
              {VALUE_PROPS.map((v) => (
                <div key={v.title}>
                  <v.icon size={26} strokeWidth={1.25} className="text-black" />
                  <h3 className="font-display text-xl mt-5 tracking-tight">{v.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed mt-2">{v.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 md:py-32 bg-[#FCFCFC]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <p className="overline mb-4">Our Process</p>
          <h2 className="font-display text-4xl md:text-5xl tracking-tighter font-light max-w-3xl mb-14">
            From first browse to the keys in your hand.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-4">
            {PROCESS.map((p, i) => (
              <div key={p.step} className="md:border-l md:border-gray-200 md:pl-6 pt-4">
                <span className="font-display text-3xl tracking-tighter text-[#9D8C70]">{p.step}</span>
                <h3 className="font-display text-xl mt-3 tracking-tight">{p.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed mt-2">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interior split */}
      <section className="bg-white">
        <div className="grid grid-cols-1 md:grid-cols-2">
          <div className="aspect-[4/3] md:aspect-auto">
            <img src={INTERIOR_IMG} alt="Luxury interior" className="w-full h-full object-cover" />
          </div>
          <div className="flex items-center px-6 md:px-12 lg:px-20 py-20 md:py-0">
            <div className="max-w-md">
              <p className="overline mb-4">For The Connoisseur</p>
              <h2 className="font-display text-4xl md:text-5xl tracking-tighter font-light">
                Sell with the discretion you deserve.
              </h2>
              <p className="text-gray-600 leading-relaxed mt-6">
                A private valuation, an off-market sale, settlement within days. Royale Yaan is built for
                owners who expect their automobile to be handled with as much care as they purchased it.
              </p>
              <Link to="/sell" data-testid="sell-section-cta" className="inline-flex items-center gap-2 mt-8 px-7 py-4 text-sm tracking-wide btn-primary rounded-sm">
                Request a Valuation <ChevronRight size={15} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 md:py-32 bg-[#FCFCFC]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <p className="overline mb-4">Owners&apos; Voices</p>
          <h2 className="font-display text-4xl md:text-5xl tracking-tighter font-light max-w-3xl mb-14">
            Trusted by India&apos;s most discerning drivers.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="bg-white border border-gray-200 p-8 md:p-10">
                <p className="text-gray-800 leading-relaxed text-[15px]">&ldquo;{t.text}&rdquo;</p>
                <div className="mt-8 pt-6 border-t border-gray-100">
                  <p className="font-display text-lg tracking-tight">{t.name}</p>
                  <p className="text-xs text-gray-500 mt-1">{t.car}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-black text-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-24 md:py-32 flex flex-col md:flex-row items-start md:items-end justify-between gap-10">
          <h2 className="font-display text-4xl md:text-6xl tracking-tighter font-light max-w-3xl">
            Begin your next great drive.
          </h2>
          <div className="flex flex-wrap gap-3">
            <Link to="/inventory" className="inline-flex items-center gap-2 px-7 py-4 text-sm tracking-wide bg-white text-black rounded-sm hover:bg-gray-100 transition-colors">
              Browse Inventory <ArrowRight size={15} />
            </Link>
            <a href={WHATSAPP_LINK("Hi Royale Yaan, I'd like to explore options.")} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-7 py-4 text-sm tracking-wide border border-white/30 hover:bg-white/10 rounded-sm transition-colors">
              Talk on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
